import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  EMPTY,
  Observable,
  Subject,
  map,
  shareReplay,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { Course, Subsection } from './course.model';
import { APIResponse } from 'src/app/shared/data-access/api-response.model';
import {catchApiError} from "../../shared/utils/api-error";
import {encodeParam} from "../../shared/utils/query-string";

/*
  * In this service I want to demonstrate the differences between declarative vs inperative programming.
  * With declarative programming, the code is more readable and easier to understand. It is also easier to test.
  * You can also use the RxJS operators to compose your observables in a way that is easy to understand.
  * In addition, it is easier to chain observables when values are emitted to allow for more reactive and accurate data flow

  * I personally have a bias towards declarative, however there is no right or wrong way to do it.
  * You can delete either of the implementations and use whichever you prefer, but I figured I would show both.
*/

@Injectable({
  providedIn: 'root',
})
export class CourseService {
  private httpClient = inject(HttpClient);

  // These methods are imperative, they tell the computer how to do something, these only function when they are called leading to more complex code when you have to chain.
  loadCourses(): Observable<Course[]> {
    const apiUrl = `${environment.baseUrl}/course/list`;
    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response.success ? (response.result as Course[]) : [];
      })
    );
  }

  listCoursesByExamEditRole(): Observable<Course[]> {
    const apiUrl = `${environment.baseUrl}/course/list/examediting`;
    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response.success ? (response.result as Course[]) : [];
      })
    );
  }

  listAccommodationCourses(): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/course/list/accommodation`

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  // In order to get the subsections for a specific course, you need to make sure to call this function after the course has been selected or else you can be left with desyncronized data
  getSubsections(course_id: number): Observable<Subsection[]> {
    const apiUrl = `${environment.baseUrl}/course/subsection`;

    const params = new HttpParams().set('course_id', course_id.toString());

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response.success ? (response.result as Subsection[]) : [];
      })
    );
  }

  // These observables are declarative, they tell the computer what to do
  // Instead of having to worry about where to call the function, you can just subscribe to the observable and it will emit the value when it is ready
  private courseSelectionSubject = new Subject<number>();
  private fetchCoursesSubject = new Subject<void>();

  courses$: Observable<Course[]> = this.fetchCoursesSubject.pipe(
    switchMap(() =>
      this.httpClient
        .get<APIResponse>(`${environment.baseUrl}/course/list`)
        .pipe(
          map((response) => {
            return response.success ? (response.result as Course[]) : [];
          }),
          shareReplay(1) // This will cache the value of the observable so that it does not call the api again when others subscribe to it
        )
    )
  );

  // Using a subject to determine when a course is selected pushes the new value to the selected_course$ observable
  selected_course$: Observable<Course | null> = this.courses$.pipe(
    withLatestFrom(this.courseSelectionSubject),
    map(
      ([courses, course_id]) =>
        courses.find((course) => course.course_id === course_id) || null
    )
  );

  // When a new value is emitted from the observable, it signifies that a course has been changed and the data for that courses subsections needs to be updated.
  // This observable will call the API to get the new subsections for the selected course and emit the new value to its subscribers.
  subsections$: Observable<Subsection[]> = this.selected_course$.pipe(
    switchMap((course) => {
      if (!course) return EMPTY;
      const apiUrl = `${environment.baseUrl}/course/subsection`;
      const params = new HttpParams().set(
        'course_id',
        course.course_id.toString()
      );
      return this.httpClient.get<APIResponse>(apiUrl).pipe(
        map((response) => {
          return response.success ? (response.result as Subsection[]) : [];
        })
      );
    }),
    shareReplay(1) // Prevents the api call when others subscribe, but does not prevent the API call when a new value from selected_course$ is emitted
  );

  // Util functions

  // Since observables are meant to be read only, you can use util functions for components to call which will push values to the subject, which will then emit the new value to the observable chain
  selectCourse(course_id: number) {
    this.courseSelectionSubject.next(course_id);
  }

  refreshCourses() {
    this.fetchCoursesSubject.next();
  }

  getMySubsections(search: string, page: number, limit: number, filters: any): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/course/subsections/listmy?page=${encodeParam(page)}&limit=${encodeParam(limit)}&search=${encodeParam(search)}`;
    if(filters.start_date && filters.end_date) {
      apiUrl = `${apiUrl}&start_date=${encodeParam(encodeURIComponent(filters.start_date.toISOString()))}&end_date=${encodeParam(encodeURIComponent(filters.end_date.toISOString()))}`;
    }
    if(filters.type !== 'null') {
      apiUrl = `${apiUrl}&types=${encodeParam(filters.type)}`;
    }
    if(filters.courses !== 'null') {
      apiUrl = `${apiUrl}&courses=${encodeParam(filters.courses)}`;
    }

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  getAllSubsections(search: string, page: number, limit: number, filters: any): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/course/subsections/listall?page=${encodeParam(page)}&limit=${encodeParam(limit)}&search=${encodeParam(search)}`;
    if(filters.start_date && filters.end_date) {
      apiUrl = `${apiUrl}&start_date=${encodeParam(encodeURIComponent(filters.start_date.toISOString()))}&end_date=${encodeParam(encodeURIComponent(filters.end_date.toISOString()))}`;
    }
    if(filters.type !== 'null') {
      apiUrl = `${apiUrl}&types=${encodeParam(filters.type)}`;
    }
    if(filters.courses !== 'null') {
      apiUrl = `${apiUrl}&courses=${encodeParam(filters.courses)}`;
    }

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  getSubsectionTypes(): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/course/subsections/types`;

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  getDistinctCourseNames(): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/course/distinctnames`;

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  getMyCourses(): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/course/list/my`;
    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  createSubsection(subsection: any): Observable<APIResponse> {
    const apiUrl = `${environment.baseUrl}/course/subsections/create`;

    const body = new HttpParams().set('course_id', subsection.course_id)
      .set('name', subsection.name)
      .set('description', subsection.description)
      .set('subsection_type', subsection.type);

    return this.httpClient.post<APIResponse>(apiUrl, body).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  editSubsection(subsection_id: number, subsection: any): Observable<APIResponse> {
    const apiUrl = `${environment.baseUrl}/course/subsections/edit`;
    const body = new HttpParams().set('subsection_id', subsection_id)
      .set('course_id', subsection.course_id)
      .set('name', subsection.name)
      .set('description', subsection.description)
      .set('subsection_type', subsection.type);

    return this.httpClient.put<APIResponse>(apiUrl, body).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  deleteSubsection(subsection_id: number): Observable<APIResponse> {
    const apiUrl = `${environment.baseUrl}/course/subsections/delete?subsection_id=${encodeParam(subsection_id)}`;

    return this.httpClient.delete<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  getSubsectionStudents(subsection_id: number, page: number, limit: number, search?: string): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/course/subsections/students?subsection_id=${encodeParam(subsection_id)}&page=${encodeParam(page)}&limit=${encodeParam(limit)}`;

    if(search) {
      apiUrl += `&search=${encodeParam(search)}`;
    }

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  addStudentToSubsection(subsection_id: number, netid: string): Observable<APIResponse> {
    const apiUrl = `${environment.baseUrl}/course/subsections/students/add`;

    const body = new HttpParams().set('subsection_id', subsection_id)
      .set('netid', netid)

    return this.httpClient.post<APIResponse>(apiUrl, body).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }

  removeStudentFromSubsection(subsection_id: number, user_id: number): Observable<APIResponse> {
    const apiUrl = `${environment.baseUrl}/course/subsections/students/remove`;

    const body = new HttpParams().set('subsection_id', subsection_id)
      .set('student_id', user_id)

    const options = {
      body: body
    };

    return this.httpClient.delete<APIResponse>(apiUrl, options).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    )
  }
}
